import { cloneDeep, orderBy, some } from 'lodash';
import {
  checkTextWithSuggestions,
  initGrammalecte,
  initSpellchecker,
  nspellCheckWord,
} from '../../../../../utils/grammalecteClient';
import { removePunctuation } from '../common/functions.js';

class Grammalecte {
  ignoreWords = [];
  ignoreSuggests = [];
  lang = null;

  queues = [];
  isProcessing = false;
  maxConcurrentChecks = 3;
  wordsInError = [];
  currentPosition = -1;

  async setLang(lang) {
    switch (lang) {
      case 'fr':
      case 'fr-FR':
      case 'fr-CA':
      case 'fr-BE':
      case 'fr-CH':
        this.lang = 'fr';
        break;

      case 'en':
      case 'en-GB':
      case 'en-US':
      case 'en-CA':
        this.lang = 'en';
        break;

      default:
        this.lang = null;
        break;
    }

    this.init();

    this.wordsInError = [];
  }

  async init() {
    if (!this.lang) {
      return;
    }

    if (this.lang === 'en') {
      await initSpellchecker();
      return;
    }

    await initGrammalecte();
  }

  clear() {
    this.wordsInError = [];
    this.currentPosition = -1;
  }

  stop() {
    if (this.isProcessing) {
      this.isProcessing = false;
    }
  }

  getNextWordInError() {
    if (this.wordsInError.length === 0) {
      return null;
    }

    if (
      this.currentPosition === -1 ||
      this.currentPosition === this.wordsInError.length - 1
    ) {
      this.currentPosition = 0;
    } else {
      this.currentPosition++;
    }

    return this.wordsInError[this.currentPosition];
  }

  // ***************************************************************************************
  // convertSubtitleToSentences() permet de convertir le sous-titre en phrases
  // ***************************************************************************************
  convertSubtitleToSentences(subtitle) {
    const sentences = [];

    subtitle.lines.forEach((line, index) => {
      const sentence = line.words.map((x) => x.text).join(' ');

      if (sentence.length > 0) {
        sentences.push({
          subtitleId: subtitle.id,
          indexLine: index,
          words: line.words.map((x) => cloneDeep(x)),
          sentence,
        });
      }
    });

    return sentences;
  }

  enqueueCheck(subtitles) {
    return new Promise((resolve, reject) => {
      this.queues.push({
        subtitles,
        resolve,
        reject,
      });
      this.processQueue();
    });
  }

  // Traiter la queue
  async processQueue() {
    if (this.isProcessing || this.queues.length === 0) {
      return;
    }

    this.isProcessing = true;

    const tasks = [];
    const maxTasks = Math.min(this.maxConcurrentChecks, this.queues.length);

    for (let i = 0; i < maxTasks; i++) {
      const task = this.queues.shift();
      if (task) {
        try {
          const result = await this.checkSubtitles(task.subtitles);
          task.resolve(result);
        } catch (error) {
          task.reject(error);
        }
      }
    }

    await Promise.all(tasks);

    this.isProcessing = false;

    // Continuer le traitement s'il reste des tâches
    if (this.queues.length > 0) {
      this.processQueue();
    }
  }

  // ***************************************************************************************
  // checkSubtitles() permet de vérifier les sous-titres
  // ***************************************************************************************
  async checkSubtitles(subtitles) {
    if (this.lang === 'en') {
      return await this.checkSubtitlesEnglish(subtitles);
    }

    const results = [];

    for (const subtitle of subtitles) {
      if (!this.isProcessing) {
        break;
      }

      const sentencesToCheck = this.convertSubtitleToSentences(subtitle);

      for (const sentenceToCheck of sentencesToCheck) {
        try {
          const sentence = sentenceToCheck.sentence.replaceAll(`'`, '’');

          const result = await checkTextWithSuggestions(sentence);

          // Traiter les résultats et les associer aux mots
          for (const para of result) {
            // Associer les erreurs aux mots de la ligne
            if (
              para.aSpellErrWithSuggestions &&
              para.aSpellErrWithSuggestions.length > 0
            ) {
              for (const error of para.aSpellErrWithSuggestions) {
                // Trouver le mot correspondant dans sentence.words
                const matchingWord = sentenceToCheck.words.find(
                  (word) =>
                    word.text === error.sValue ||
                    word.text.includes(error.sValue)
                );

                if (matchingWord) {
                  if (
                    this.ignoreWords.includes(
                      removePunctuation(matchingWord.text.toLowerCase())
                    )
                  ) {
                    continue;
                  }

                  const suggest = {
                    suggests: orderBy(error.suggestions, (x) => x),
                    underlined: error.sValue,
                    errorType: 'spelling',
                    subtitleId: sentenceToCheck.subtitleId,
                  };

                  if (some(this.ignoreSuggests, suggest)) {
                    continue;
                  }

                  const errorWord = {
                    subtitleId: sentenceToCheck.subtitleId,
                    indexLine: sentenceToCheck.indexLine,
                    suggest,
                    in: matchingWord.in,
                    out: matchingWord.out,
                    text: matchingWord.text,
                  };

                  if (!this.wordsInError.includes(errorWord)) {
                    this.wordsInError.push(errorWord);
                    results.push(errorWord);
                  }
                }
              }
            }

            if (para.aGrammErr && para.aGrammErr.length > 0) {
              // Gérer les erreurs grammaticales
              for (const error of para.aGrammErr) {

                let matchingWord;
                let nbMatchingWords = 0;

                let index = 0;
                const matchingWords = [];
                for (const word of sentenceToCheck.words) {
                  for (const letter of Array.from(word.text)) {
                    if (index >= error.nStart && index <= error.nEnd) {
                      if (!matchingWords.includes(word)) {
                        matchingWords.push(word);
                      }
                    }

                    index++;
                  }

                  // avec l'espace en plus
                  index++;

                  if (index >= error.nStart && index <= error.nEnd) {
                    if (!matchingWords.includes(word)) {
                      matchingWords.push(word);
                    }
                  }
                }

                matchingWord = matchingWords[0];
                nbMatchingWords = matchingWords.length;

                if (matchingWord) {
                  if (
                    this.ignoreWords.includes(
                      removePunctuation(matchingWord.text.toLowerCase())
                    )
                  ) {
                    continue;
                  }

                  const suggest = {
                    suggests: orderBy(error.aSuggestions, (x) => x),
                    message: error.sMessage,
                    underlined: error.sUnderlined,
                    nbMatchingWords,
                    errorType: 'grammar',
                    subtitleId: sentenceToCheck.subtitleId,
                  };

                  if (some(this.ignoreSuggests, suggest)) {
                    continue;
                  }

                  const errorWord = {
                    subtitleId: sentenceToCheck.subtitleId,
                    indexLine: sentenceToCheck.indexLine,
                    suggest,
                    in: matchingWord.in,
                    out: matchingWord.out,
                    text: matchingWord.text,
                  };

                  if (!this.wordsInError.includes(errorWord)) {
                    this.wordsInError.push(errorWord);
                    results.push(errorWord);
                  }
                }
              }
            }
          }
        } catch (error) {
          console.error(
            'Erreur lors de la vérification de la phrase :',
            sentenceToCheck.sentence,
            error
          );
        }
      }
    }

    return orderBy(results, ['subtitleId', 'indexLine']);
  }

  async checkSubtitlesEnglish(subtitles) {
    await initSpellchecker();

    const results = [];

    for (const subtitle of subtitles) {
      if (!this.isProcessing) {
        break;
      }

      if (!subtitle.lines.length) {
        continue;
      }

      let indexLine = 0;

      for (const line of subtitle.lines) {
        for (const word of line.words) {
          if (word.suggest) {
            word.suggest = undefined;
          }

          const wordsSplitted = word.text.split(' ');

          for (const wordSplitted of wordsSplitted) {
            if (!this.isProcessing) {
              break;
            }

            const wordWithoutPunctuation = removePunctuation(wordSplitted);

            const result = nspellCheckWord(wordWithoutPunctuation);

            if (result.correct === false) {
              if (
                this.ignoreWords.includes(
                  removePunctuation(wordWithoutPunctuation.toLowerCase())
                )
              ) {
                return;
              }

              const startOffset = word.text.indexOf(wordWithoutPunctuation);

              const suggest = {
                suggests: orderBy(result.suggestions, (x) => x),
                errorType: 'spelling-en',
                underlined: wordWithoutPunctuation,
                subtitleId: subtitle.id,
              };

              if (some(this.ignoreSuggests, suggest)) {
                continue;
              }

              const errorWord = {
                suggest,
                subtitleId: subtitle.id,
                indexLine,
                in: word.in,
                out: word.out,
                text: word.text,
              };

              this.wordsInError.push(errorWord);
              results.push(errorWord);
            }
          }
        }

        indexLine++;
      }
    }

    return results;
  }

  // ***************************************************************************************
  // ignoreWord() permet d'ignorer le mot dans toutes les suggestions
  // ***************************************************************************************
  ignoreWord(text) {
    const textLc = text.toLowerCase();
    if (!this.ignoreWords.includes(textLc)) {
      this.ignoreWords.push(textLc);
    }

    // S'il contient d'autres mots qui ont ce texte
    if (
      this.wordsInError.some(
        (x) => x.suggest.underlined.toLowerCase() === text.toLowerCase()
      )
    ) {
      if (this.onIgnoreWordsChange) {
        // Appeler le callback si défini
        this.onIgnoreWordsChange(this.ignoreWords);
      }
    }
  }

  // ***************************************************************************************
  // ignoreSuggestWord() permet d'ignorer le mot dans toutes les suggestions
  // ***************************************************************************************
  ignoreSuggestWord(word) {
    this.ignoreSuggests.push(word.suggest);
  }
}

export default Grammalecte;
